/// Top-level shell: theme CSS vars, session restore, splash, and the router.

import { useEffect } from 'react';
import { BrowserRouter } from 'react-router';

import { startAnalyticsSession, heartbeat } from '@/analytics/analyticsClient';
import { startRemoteConfigPolling, useRemoteConfigStore } from '@/app/remote_config/handler/remoteConfigStore';
import { AppRouter } from '@/app/router/AppRouter';
import { AppLoadingScreen } from '@/app/ui/AppLoadingScreen';
import { AppPixelFrame } from '@/app/ui/AppPixelFrame';
import { SnackbarHost } from '@/app/ui/snackbar/SnackbarHost';
import { useAuthStore } from '@/auth/handler/authStore';
import { startConnectivityWatch } from '@/connectivity/handler/connectivityStore';
import { useIsDarkMode, useThemeColors, startSystemThemeWatch } from '@/settings/handler/settingsStore';

export function App() {
  const isDark = useIsDarkMode();
  const colors = useThemeColors();
  const restore = useAuthStore((state) => state.restore);
  const isRestoring = useAuthStore((state) => state.isRestoring);
  const remoteConfigReady = useRemoteConfigStore((state) => state.ready);

  useEffect(() => {
    startRemoteConfigPolling();
    startConnectivityWatch();
    startAnalyticsSession();
    const onVisible = (): void => {
      if (!document.hidden) void heartbeat(false);
    };
    document.addEventListener('visibilitychange', onVisible);
    const stopTheme = startSystemThemeWatch();
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      stopTheme();
    };
  }, []);

  useEffect(() => {
    void restore();
  }, [restore]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--app-surface', colors.surface);
    root.style.setProperty('--app-on-surface', colors.onSurface);
    root.style.colorScheme = isDark ? 'dark' : 'light';
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', colors.surface);
  }, [colors, isDark]);

  // Hold splash until auth restore + first /v1/config attempt so we never
  // paint home then bounce to maintenance / force-update.
  const showSplash = isRestoring || !remoteConfigReady;

  return (
    <div
      className="h-full min-h-full"
      style={{ backgroundColor: colors.surface, color: colors.onSurface }}
    >
      {showSplash ? (
        <AppLoadingScreen isDark={isDark} />
      ) : (
        <BrowserRouter>
          <SnackbarHost>
            <AppPixelFrame>
              <AppRouter />
            </AppPixelFrame>
          </SnackbarHost>
        </BrowserRouter>
      )}
    </div>
  );
}
